import {
  Button,
  ButtonProps,
  ConfirmModal,
  Flex,
  Heading,
  Modal,
} from '@conteg/ui';
import Notification from 'components/notification/notification';
import { PolicyGuardWrapper } from 'components/policy-guard/policy-guard';
import { IS_KIOSK } from 'config';
import ActionsModalContent from 'pages/storage-unit-detail/components/actions/actions-modal-content';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StorageUnitDtoFragment,
  StorageUnitStatus,
} from 'types/generated/graphql';
import { PolicyAction } from 'types/policies-types';
import { useUserIdProvider } from 'utils/hooks/use-user-id-provider';

export enum OpenModalTypes {
  OpenStorageUnit = 'openStorageUnit',
  RequestMaintenance = 'requestMaintenance',
  UpdateStatus = 'updateStatus',
  ShowOtp = 'showOtp',
  TerminateReservation = 'terminateReservation',
  ResendAccessCode = 'resendAccessCode',
  OpenStorageUnitWithoutPin = 'openStorageUnitWithoutPin',
  UpdateReservationState = 'updateReservationState',
  ChangeBlockingReservationTenantSubjectId = 'changeBlockingReservationTenantSubjectId',
  ForcedOpenStorageUnitWithReservation = 'forcedOpenStorageUnitWithReservation',
}

export type OpenModal =
  | OpenModalTypes.OpenStorageUnit
  | OpenModalTypes.RequestMaintenance
  | OpenModalTypes.UpdateStatus
  | OpenModalTypes.ShowOtp
  | OpenModalTypes.TerminateReservation
  | OpenModalTypes.ResendAccessCode
  | OpenModalTypes.OpenStorageUnitWithoutPin
  | OpenModalTypes.UpdateReservationState
  | OpenModalTypes.ChangeBlockingReservationTenantSubjectId
  | OpenModalTypes.ForcedOpenStorageUnitWithReservation
  | null;

export type UnitStatus = StorageUnitStatus | undefined | null;

type Devices = 'web' | 'kiosk' | 'both';

type ReservationActionsProps = {
  storageUnit: StorageUnitDtoFragment;
};

type Action = {
  id: string;
  label: string;
  onClick: () => void;
  availableOn: Devices;
  isAvailable?: boolean;
  variant: 'primary' | 'secondary' | 'danger';
  policyAction?: PolicyAction;
};

const isActionAvailable = (
  actionDevice: Devices,
  currentDevice: Devices,
  isAvailable?: boolean
): boolean => {
  return (
    (actionDevice === currentDevice || actionDevice === 'both') &&
    (isAvailable === undefined || isAvailable)
  );
};

const ReservationActions = ({ storageUnit }: ReservationActionsProps) => {
  const { t } = useTranslation();
  const isProvider = useUserIdProvider();

  const deviceType = IS_KIOSK ? 'kiosk' : 'web';

  const [actionResult] = useState<string>('');
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [showModalCloseConfirmation, setShowModalCloseConfirmation] =
    useState<boolean>(false);

  const hasReservation =
    !!storageUnit.allocation || !!storageUnit.firstUpcomingReservation;

  // An action finished in the modal - a pickup the gateway recorded with the open, a termination - ends the
  // reservation, and the unit refetched afterwards has none. The modal keeps the unit it was opened for, so the
  // operator still sees the outcome instead of the modal vanishing with the reservation.
  const [modalStorageUnit, setModalStorageUnit] = useState(storageUnit);
  if (hasReservation && modalStorageUnit !== storageUnit) {
    setModalStorageUnit(storageUnit);
  }

  const isReservedState =
    !!storageUnit.firstUpcomingReservation && !storageUnit.allocation;

  const shouldHideForExternalReservation = isReservedState && !isProvider;

  // The parcel is protected by a recipient release code (UPS EPRA) that nobody has verified yet. Every
  // action below that could hand it over anyway - a forced open, moving the reservation's state,
  // cancelling it - then needs the dedicated role instead of its usual one. Once a code has been
  // verified the parcel is legitimately releasable and the ordinary permissions apply again. The gateway
  // enforces the same rule; this only decides which buttons are worth showing.
  const reservation = storageUnit.allocation?.reservation;
  const isTwoFactorPending =
    !!reservation?.is2FaRequired && !reservation?.twoFactorVerifiedAt;

  // Plain array, not useState: a state initialiser runs once, which would freeze every flag below at the
  // first render - including the two-factor one, and a permission decision must not be computed from data that
  // had not arrived yet.
  const availableActions: Action[] = [
    {
      id: 'open',
      label: 'Page.StorageUnit.Actions.Open.Reservation',
      policyAction: 'openStorageUnitWithReservation',
      onClick: () =>
        setOpenModal(
          storageUnit.allocation?.reservation?.isBlockingReservation
            ? OpenModalTypes.OpenStorageUnitWithoutPin
            : OpenModalTypes.OpenStorageUnit
        ),
      variant: 'primary',
      availableOn: 'both',
      isAvailable:
        (deviceType === 'web' || !!storageUnit.allocation) &&
        !storageUnit.isExistingReservationNeverEnding,
    },
    {
      id: 'forced-open',
      label: 'Page.StorageUnit.Actions.ForcedOpen',
      policyAction: isTwoFactorPending
        ? 'manageTwoFactorReservation'
        : 'forcedStorageOpen',
      onClick: () =>
        setOpenModal(OpenModalTypes.ForcedOpenStorageUnitWithReservation),
      variant: 'danger',
      availableOn: 'both',
      isAvailable: !!storageUnit.allocation,
    },
    {
      id: 'update-reservation-state',
      label: 'Page.StorageUnit.Actions.UpdateReservationState',
      policyAction: isTwoFactorPending
        ? 'manageTwoFactorReservation'
        : 'editReservation',
      onClick: () => setOpenModal(OpenModalTypes.UpdateReservationState),
      variant: 'primary',
      availableOn: 'both',
      isAvailable:
        !shouldHideForExternalReservation &&
        !storageUnit.allocation?.reservation?.isBlockingReservation,
    },
    {
      id: 'change-blocking-reservation-tenant-subject-id',
      label:
        'Page.StorageUnit.Actions.ChangeBlockingReservationTenantSubjectId',
      policyAction: 'editReservation',
      onClick: () =>
        setOpenModal(OpenModalTypes.ChangeBlockingReservationTenantSubjectId),
      variant: 'primary',
      availableOn: 'both',
      isAvailable:
        !shouldHideForExternalReservation &&
        (storageUnit.allocation?.reservation?.isBlockingReservation ||
          storageUnit.firstUpcomingReservation?.isBlockingReservation),
    },
    {
      id: 'terminate',
      label: 'Page.StorageUnit.Actions.Terminate.Reservation',
      policyAction: isTwoFactorPending
        ? 'manageTwoFactorReservation'
        : 'forcedReservationTerminate',
      onClick: () => setOpenModal(OpenModalTypes.TerminateReservation),
      variant: 'danger',
      availableOn: 'both',
      isAvailable:
        !!storageUnit.allocation || !!storageUnit.firstUpcomingReservation,
    },
    {
      id: 'resend-access-code',
      label: 'Page.StorageUnit.Actions.ResendAccessCode',
      policyAction: 'editReservation',
      onClick: () => setOpenModal(OpenModalTypes.ResendAccessCode),
      variant: 'primary',
      availableOn: 'both',
      isAvailable:
        !shouldHideForExternalReservation &&
        // Resending notifies via every configured channel (SMS and/or email), so the action is available
        // whenever the reservation has any contact (phone or email) or there is an upcoming reservation.
        (!!storageUnit.allocation?.reservation.customerPinCredentials?.[0]
          ?.phone ||
          !!storageUnit.allocation?.reservation.customerPinCredentials?.[0]
            ?.email ||
          !!storageUnit.firstUpcomingReservation),
    },
    // TODO: uncomment when reporting will be ready
    // {
    //   id: 'block',
    //   label: 'Page.StorageUnit.Actions.Status.Block',
    //   onClick: () => setOpenModal(OpenModalTypes.UpdateStatus),
    //   variant: 'primary',
    //   availableOn: 'web',
    //   isAvailable: storageUnitStatus !== StorageUnitStatus.Broken,
    // },
    // {
    //   id: 'unblock',
    //   label: 'Page.StorageUnit.Actions.Status.Unblock',
    //   onClick: () => setOpenModal(OpenModalTypes.UpdateStatus),
    //   variant: 'primary',
    //   availableOn: 'web',
    //   isAvailable: storageUnitStatus === StorageUnitStatus.Broken,
    // },
    // {
    //   id: 'maintenance',
    //   label: 'Page.StorageUnit.Actions.Maintenance',
    //   onClick: () => setOpenModal(OpenModalTypes.RequestMaintenance),
    //   variant: 'primary',
    //   availableOn: 'web',
    //   isAvailable: !isMaintenanceRequested,
    // },
    // {
    //   id: 'redCard',
    //   label: 'Page.StorageUnit.Actions.Report.RedCard',
    //   onClick: () => alert(`Nahlásí RedCard pro: ${storageUnitId}`),
    //   variant: 'primary',
    //   availableOn: 'both',
    // },
    // {
    //   id: 'missingPackage',
    //   label: 'Page.StorageUnit.Actions.Report.MissingPackage',
    //   onClick: () => alert(`Nahlásí chybějící zásilku pro: ${storageUnitId}`),
    //   variant: 'primary',
    //   availableOn: 'both',
    // },
  ];

  return (
    <>
      {hasReservation && (
        <Flex flexDirection="column" gap="3rem" mt="5rem">
          <Heading variant="h3" title={t('Page.StorageUnit.Actions.Heading')} />
          {actionResult && (
            <Notification testId="action-result" message={actionResult} />
          )}
          <Flex gap="3rem">
            {availableActions
              .filter((action) =>
                isActionAvailable(
                  action.availableOn,
                  deviceType,
                  action.isAvailable
                )
              )
              .map((action) => {
                const buttonProps: ButtonProps = {
                  testId: `action=${action.id}`,
                  variant: action.variant,
                  size: 'l',
                  title: t(action.label),
                  onClick: action.onClick,
                };

                if (action.policyAction) {
                  return (
                    <PolicyGuardWrapper
                      key={action.id}
                      action={action.policyAction}
                    >
                      <Button {...buttonProps} />
                    </PolicyGuardWrapper>
                  );
                }
                return <Button key={action.id} {...buttonProps} />;
              })}
          </Flex>
        </Flex>
      )}
      <Modal
        testId="unit-actions-modal"
        size="xl"
        showCloseButton={true}
        isOpen={openModal !== null}
        onClose={() => setShowModalCloseConfirmation(true)}
      >
        <ActionsModalContent
          storageUnit={modalStorageUnit}
          contentType={openModal}
          onClose={() => setShowModalCloseConfirmation(true)}
          onDirectClose={() => setOpenModal(null)}
        />
      </Modal>
      <ConfirmModal
        zIndexOffset={1000}
        modalTitle={t('Page.StorageUnit.Actions.CloseAction.ConfirmTitle')}
        modalDescription={t('Page.StorageUnit.Actions.CloseAction.ConfirmText')}
        isOpen={showModalCloseConfirmation}
        confirm={{
          text: t('Page.StorageUnit.Actions.CloseAction.ConfirmButton'),
          variant: 'danger',
        }}
        cancelText={t('Page.StorageUnit.Actions.CloseAction.CancelButton')}
        onClose={() => setShowModalCloseConfirmation(false)}
        onConfirm={() => {
          setOpenModal(null);
          setShowModalCloseConfirmation(false);
        }}
      />
    </>
  );
};

export default ReservationActions;
